import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Navbar as BNavbar,
  Container,
  Nav,
  NavDropdown,
} from "react-bootstrap";
import { ExclamationTriangleFill, FlagFill } from "react-bootstrap-icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useSessionStorage } from "usehooks-ts";
import {
  type Announcement,
  Role,
  SubscriptionPlan,
  SubscriptionStatus,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import { useAnnouncementApi, useFirmApi, useStripeApi } from "@/hooks/useApi";
import { OnBehalfOfSessionKey, useAuth } from "@/hooks/useAuth";
import { useProvideOAuth } from "@/hooks/useOAuth";
import logo from "@/images/logo-wordmark-reverse.svg";
import { getFirmSubscriptionOptions } from "@/lib/query-options";
import { id } from "@/lib/utils";
import Notifications from "./Notifications";

interface Props {
  fluid?: true | "sm" | "md" | "lg" | "xl" | "xxl";
}

interface AccountLinkProps {
  isLoginPage?: boolean;
  announcements: Announcement[];
}

const AccountLink = ({ isLoginPage, announcements }: AccountLinkProps) => {
  const { isAuthenticated, logout } = useProvideOAuth();
  const { user, firm } = useAuth();
  const stripeApi = useStripeApi();

  async function handleGetPortalUrl() {
    const url = await stripeApi.stripePortalSession();
    window.location.href = url;
  }

  const showFirmAdmin =
    user && firm && user.role && user.role.includes(Role.FirmAdmin);

  return isAuthenticated ? (
    <div className="nav-user-links">
      <div className="desktop-notifications">
        {user && <Notifications user={user} announcements={announcements} />}
      </div>
      {showFirmAdmin && (
        <NavDropdown
          title={
            <>
              <span className="firm-name-tablet">Firm</span>
              <span className="firm-name-desktop">{firm?.name || "Firm"}</span>
            </>
          }
          id="collapsible-nav-dropdown"
          align="end"
        >
          {user && firm && (
            <>
              <NavDropdown.Item as={Link} to="/firm">
                Users
              </NavDropdown.Item>
              {firm.billingInfo &&
                firm.billingInfo.subscriptionPlan !== SubscriptionPlan.None && (
                  <NavDropdown.Item onClick={handleGetPortalUrl}>
                    Billing
                  </NavDropdown.Item>
                )}
            </>
          )}
        </NavDropdown>
      )}
      <NavDropdown
        title={user?.firstName || "Account"}
        id="collapsible-nav-dropdown"
        align="end"
      >
        {user && firm && (
          <NavDropdown.Item as={Link} to="/general-info">
            Information
          </NavDropdown.Item>
        )}
        <NavDropdown.Item onClick={() => logout(window.location.origin)}>
          Logout
        </NavDropdown.Item>
      </NavDropdown>
    </div>
  ) : !isLoginPage ? (
    <Nav.Link as={Link} to="/login">
      Login
    </Nav.Link>
  ) : null;
};

function AdminLink() {
  return (
    <NavDropdown title="Admin" id="collapsible-nav-dropdown">
      <NavDropdown.Item as={Link} to="/admin/documents">
        Documents
      </NavDropdown.Item>
      <NavDropdown.Item as={Link} to="/admin">
        Users
      </NavDropdown.Item>
      <NavDropdown.Item as={Link} to="/admin/Firm">
        Firms
      </NavDropdown.Item>
      <NavDropdown.Item as={Link} to="/admin/revenue">
        Revenue
      </NavDropdown.Item>
      <NavDropdown.Item as={Link} to="/admin/misc">
        Misc
      </NavDropdown.Item>
    </NavDropdown>
  );
}

export default function Navbar({ fluid }: Props) {
  const { isAuthenticated } = useProvideOAuth();
  const location = useLocation();
  const { user, baseUser } = useAuth();
  const firmApi = useFirmApi();

  const firmSubscriptionQuery = useQuery({
    ...getFirmSubscriptionOptions(firmApi, user?.firmId ?? ""),
    select: (data) => {
      return (
        data.billingInfo.subscriptionStatus === SubscriptionStatus.Active ||
        data.billingInfo.subscriptionStatus === SubscriptionStatus.Trialing
      );
    },
    enabled: !!user?.firmId,
  });

  const announcementsApi = useAnnouncementApi();
  const [announcements, setAnnouncements] = useState<Announcement[]>();
  const [, setOnBehalfOf] = useSessionStorage(OnBehalfOfSessionKey, undefined);

  const links = getLinks(user && isAuthenticated);
  const history = useHistory();
  const APP_VERSION: string = import.meta.env.VITE_VERSION;
  const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    const get = async () => {
      if (!announcements) {
        const result = await announcementsApi.announcementGet({});
        setAnnouncements(result);
      }
    };
    get();
  }, [announcementsApi, announcements]);

  function clearOnBehalfOf() {
    setOnBehalfOf(undefined);
    if (baseUser?.role?.includes(Role.BriefpointAdmin)) {
      history.push("");
    }
  }

  const maintenanceAnnouncement = (announcements ?? []).find((x) =>
    x.content?.includes("Scheduled Maintenance"),
  );

  return (
    <>
      <BNavbar expand="md" sticky="top" bg="dark" variant="dark">
        <Container fluid={fluid}>
          <BNavbar.Brand href="/">
            <img src={logo} alt="logo" className="logo" />
            {id(ENVIRONMENT) !== "Production" && (
              <small className="alpha-version-text">v{APP_VERSION}</small>
            )}
          </BNavbar.Brand>
          {/* Notifications bell icon - shown on mobile next to toggle */}
          <div className="navbar-notifications-toggle-container">
            <div className="navbar-notifications-mobile">
              {user && isAuthenticated && (
                <Notifications
                  user={user}
                  announcements={announcements ?? []}
                />
              )}
            </div>
            <BNavbar.Toggle aria-controls="basic-navbar-nav" />
          </div>
          <BNavbar.Collapse id="basic-navbar-nav">
            <Nav activeKey={location.pathname}>
              {links.map((link) => {
                const pathname = link.path.split("?")[0];
                const firstDirectory = `/${location.pathname.split("/")?.[1] || ""}`;
                const isActive =
                  pathname === firstDirectory ||
                  (pathname === "/documents" && location.pathname === "/");

                return (
                  <Nav.Link
                    style={{ whiteSpace: "nowrap" }}
                    key={link.path}
                    as={Link}
                    to={link.path}
                    eventKey={link.path}
                    active={isActive}
                    data-testid={`navbar-link-${link.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.label}
                  </Nav.Link>
                );
              })}
              {user &&
                isAuthenticated &&
                user &&
                user.role?.includes(Role.BriefpointAdmin) && <AdminLink />}
            </Nav>

            <Nav>
              <AccountLink
                isLoginPage={location.pathname === "/login"}
                announcements={announcements ?? []}
              />
            </Nav>
          </BNavbar.Collapse>
        </Container>
      </BNavbar>
      {maintenanceAnnouncement && (
        <div className="maintenance-banner">
          <ExclamationTriangleFill className="icon" />
          &nbsp;{maintenanceAnnouncement.content}
        </div>
      )}
      {user && baseUser && user?.id !== baseUser?.id && (
        <div className="onbehalfof-banner">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ lineHeight: "2" }}
          >
            <FlagFill className="me-2" />
            {`You are working on behalf of ${user.firstName} ${user.lastName}`}
            <Button
              variant="link"
              onClick={clearOnBehalfOf}
              className="p-0 ps-3"
            >
              Change Attorney
            </Button>
          </div>
        </div>
      )}
      {firmSubscriptionQuery.isSuccess && !firmSubscriptionQuery.data && (
        <div className="subscription-inactive-banner d-flex justify-content-start align-items-center">
          <div className="subscription-inactive-banner-container">
            <ExclamationTriangleFill
              color="var(--warning)"
              className="icon me-2"
            />
            <span className="fw-bold me-1">Account Update Required:</span>Some
            actions are unavailable. Please contact your firm administrator for
            more information, or contact{" "}
            <a href="mailto:support@briefpoint.ai">support@briefpoint.ai</a> for
            help.
          </div>
        </div>
      )}
    </>
  );
}

function getLinks(isAuthenticated?: boolean) {
  const links = [];
  if (isAuthenticated) {
    links.push({ path: "/documents", label: "Document List" });
    links.push({ path: "/library?tab=objections", label: "Library" });
    links.push({ path: "/cases", label: "Cases" });
    links.push({ path: "/clients", label: "Clients" });
    links.push({ path: "/tutorials", label: "Tutorials" });
  }
  return links;
}
